import {Autocomplete, Box, Grid, TextField, Typography} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {useEffect, useMemo, useState} from 'react';
// import {deliveryServiceCreate, deliveryServiceUpdate} from 'src/apis';
import {deliveryServiceCreate, deliveryServiceUpdate} from 'src/apis/autoGenerate/services';
// import {Delivery} from 'src/apis/autoGenerate';
import {Delivery} from 'src/apis/autoGenerate/types';
import Modal from 'src/components/Modal';
import message from 'src/components/Message';
import {useApartmentBuildingUnit} from 'src/hooks';
import {AutocompleteVal} from 'src/components/SearchApartment';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';

interface AddUserModalProps {
  open: boolean;
  handleConfirm: (data?: any) => void;
  handleCancel: VoidFunction;
  initData?: Delivery;
}

function getInitFormData() {
  return {
    name: '',
    validDateFrom: '',
    validDateTo: '',
    pin: '',
    apartmentId: '',
  };
}

export default function AddDeliveryModal({
  open,
  initData,
  handleConfirm,
  handleCancel,
}: AddUserModalProps) {
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(selectAccount);
  const {
    apartmentOptions,
    selectedApartmentValue,
    setSelectedApartment,
    handleSelectApartment: _handleSelectApartment,
  } = useApartmentBuildingUnit();
  const [formData, setFormData] = useState(getInitFormData());

  useEffect(() => {
    if (initData) {
      setFormData({
        name: initData.name as string,
        validDateFrom: initData.validDateFrom as string,
        validDateTo: initData.validDateTo as string,
        pin: initData.pin as string,
        apartmentId: initData.apartmentId as string,
      });
      setSelectedApartment({
        label: initData.apartmentId as string,
        value: initData.apartmentId,
      });
    }
  }, [initData, setSelectedApartment]);

  const handleSelectApartment = (e: any, val: AutocompleteVal) => {
    _handleSelectApartment(e, val);
    setFormData({...formData, apartmentId: val?.value});
  };

  const handleAddDelivery = () => {
    setLoading(true);

    const orgId = account?.userDetail?.organizations?.[0]?.id;

    if (!orgId) {
      message.error('Organization ID not found. Please check your account settings.');
      setLoading(false);
      return;
    }

    deliveryServiceCreate({
      delivery: {
        ...formData,
        orgId,
      },
    })
      .then(res => {
        message.success('Add Success');
        handleConfirm(true);
      })
      .catch(err => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditDelivery = () => {
    // Don't make API call here, just pass the formData to parent
    handleConfirm(formData);
  };

  const isEdit = useMemo(() => {
    return !!initData;
  }, [initData]);

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Delivery' : 'Add Delivery'}
      handleCancel={handleCancel}
      handleConfirm={() => {
        isEdit ? handleEditDelivery() : handleAddDelivery();
      }}
      loading={loading}
      modalContent={
        <Box component="form">
          {/* Delivery Name */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Delivery Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Delivery Name"
            variant="outlined"
            value={formData.name}
            onChange={e => {
              setFormData(prev => ({
                ...prev,
                name: e.target.value,
              }));
            }}
            sx={{mb: 3}}
          />

          {/* Search Property */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Search property
          </Typography>
          <Autocomplete
            size="small"
            options={apartmentOptions}
            fullWidth
            value={selectedApartmentValue}
            onChange={handleSelectApartment}
            renderInput={params => (
              <TextField
                {...params}
                placeholder="Search property"
                variant="outlined"
                size="small"
              />
            )}
            sx={{mb: 3}}
          />

          {/* Date Range */}
          <Grid container spacing={3} sx={{mb: 3}}>
            <Grid item xs={6}>
              <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
                Valid From
              </Typography>
              <DatePicker
                sx={{width: '100%'}}
                value={isEdit ? dayjs(formData.validDateFrom) : null}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    placeholder: 'Valid From',
                  },
                }}
                onChange={(newValue: any) => {
                  if (newValue && newValue.$d) {
                    setFormData(prev => ({
                      ...prev,
                      validDateFrom: dayjs(newValue.$d).toISOString(),
                    }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
                Valid Till
              </Typography>
              <DatePicker
                sx={{width: '100%'}}
                value={isEdit ? dayjs(formData.validDateTo) : null}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    placeholder: 'Valid Till',
                  },
                }}
                onChange={(newValue: any) => {
                  if (newValue && newValue.$d) {
                    setFormData(prev => ({
                      ...prev,
                      validDateTo: dayjs(newValue.$d).toISOString(),
                    }));
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* PIN */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            PIN
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="PIN"
            variant="outlined"
            value={formData.pin}
            onChange={e => {
              setFormData(prev => ({
                ...prev,
                pin: e.target.value,
              }));
            }}
          />
        </Box>
      }
    />
  );
}
